/**
 * Email confirmation landing page (`/verify-email?token=xxx`).
 *
 * Hit when the user clicks the link in their inbox. Calls the existing
 * `POST /api/auth/verify-email` endpoint which marks `email_verified=true`
 * on the user and burns the token.
 *
 * Idempotent — re-visiting with the same (now-deleted) token shows a friendly
 * "already confirmed" state rather than an error.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { useI18n } from '@/contexts/I18nContext';
import { CheckCircle2, AlertCircle, Loader2, ArrowRight } from 'lucide-react';

const T = {
  is: {
    titleLoading: 'Staðfestum netfangið…',
    titleOk: 'Netfangið er staðfest',
    titleErr: 'Tókst ekki að staðfesta',
    bodyOk: 'Opnaðu Rektra af heimaskjánum til að halda áfram. Áskriftin getur byrjað og reikningar berast á þetta netfang.',
    bodyErrInvalid: 'Þessi tengill er útrunninn eða hefur þegar verið notaður. Smelltu á "Senda aftur" í forritinu til að fá nýjan staðfestingarpóst.',
    bodyErrGeneric: 'Eitthvað fór úrskeiðis. Reyndu aftur eða hafðu samband við stuðning.',
    backToApp: 'Opna Rektra',
    goLogin: 'Innskráning',
  },
  en: {
    titleLoading: 'Confirming your email…',
    titleOk: 'Email confirmed',
    titleErr: 'Could not confirm',
    bodyOk: 'Open Rektra from your home screen to continue. The subscription can start and invoices will arrive at this address.',
    bodyErrInvalid: 'This link is expired or already used. Click "Resend" in the app to get a fresh confirmation email.',
    bodyErrGeneric: 'Something went wrong. Try again or contact support.',
    backToApp: 'Open Rektra',
    goLogin: 'Log in',
  },
};

export default function VerifyEmailPage() {
  const { lang } = useI18n();
  const t = T[lang] || T.en;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // 'loading' | 'ok' | 'invalid' | 'error'
  // Ref guard so the verify POST fires exactly once per mount — without this,
  // useSearchParams() returns a fresh object on every render and re-fires the
  // useEffect, hitting the endpoint 2-3 times. The first call succeeds (and
  // burns the token), the next ones return 400, and the last 400 stomps the
  // success state, sending the user to the login page. Single-fire fixes it.
  const hasRunRef = useRef(false);

  // Extract the token outside the effect so the effect dep is the primitive
  // string (stable) instead of the URLSearchParams reference (unstable).
  const token = params.get('token');

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    if (!token) {
      setState('invalid');
      return;
    }
    // sessionStorage dedupe — survives StrictMode unmount/remount in dev AND
    // protects against an accidental page refresh consuming the token a
    // second time. Once we've successfully verified within this browser
    // session, short-circuit straight to the success screen.
    const sessionKey = `rektra_verify_${token}`;
    if (sessionStorage.getItem(sessionKey) === 'ok') {
      setState('ok');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await authAPI.verifyEmail({ token });
        try { sessionStorage.setItem(sessionKey, 'ok'); } catch (_) {}
        if (!cancelled) setState('ok');
      } catch (e) {
        if (cancelled) return;
        // If we already marked this token as ok in this session, never
        // downgrade to an error state — protects against double-fire.
        if (sessionStorage.getItem(sessionKey) === 'ok') {
          setState('ok');
          return;
        }
        const detail = e?.response?.data?.detail || '';
        if (e?.response?.status === 400 && /Ógildur|expired|útrunninn|notaður/.test(detail)) {
          setState('invalid');
        } else {
          setState('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 bg-background"
      data-testid="verify-email-page"
    >
      <div className="w-full max-w-md card p-10 sm:p-12 text-center">
        {state === 'loading' && (
          <div className="space-y-6">
            <Loader2 size={56} className="mx-auto text-accent animate-spin" />
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="verify-email-title-loading">
              {t.titleLoading}
            </h1>
          </div>
        )}

        {state === 'ok' && (
          <div className="space-y-8">
            <CheckCircle2 size={64} className="mx-auto text-accent" />
            <div className="space-y-3">
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="verify-email-title-ok">
                {t.titleOk}
              </h1>
              <p className="text-sm text-foreground-muted leading-relaxed">
                {t.bodyOk}
              </p>
            </div>
            <button
              onClick={() => { window.location.assign('/dashboard'); }}
              className="btn btn-primary w-full"
              data-testid="verify-email-cta"
            >
              {t.backToApp} <ArrowRight size={14} />
            </button>
          </div>
        )}

        {(state === 'invalid' || state === 'error') && (
          <div className="space-y-8">
            <AlertCircle size={64} className="mx-auto text-amber-400" />
            <div className="space-y-3">
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="verify-email-title-error">
                {t.titleErr}
              </h1>
              <p className="text-sm text-foreground-muted leading-relaxed">
                {state === 'invalid' ? t.bodyErrInvalid : t.bodyErrGeneric}
              </p>
            </div>
            <Link to="/login" className="btn btn-secondary w-full" data-testid="verify-email-fallback">
              {t.goLogin}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
